import { useState } from "react";
import { Fragment } from "react";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MonthInput } from "@/components/DateInput";
import { useAsyncData } from "@/lib/useAsyncData";
import { ReportError, ReportLoading } from "@/components/leasing/ReportState";
import { fmtNumber, fmtMonth } from "@/lib/format";
import { Download, ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

export default function ReportL1({ financer }) {
  const [lastMonth, setLastMonth] = useState("");
  const [projected, setProjected] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 40;

  const { loading, error, data, reload } = useAsyncData(async () => {
    const params = { financer, projected, search, page, page_size: pageSize };
    if (lastMonth) params.last_month = lastMonth;
    const res = await api.get(`/leasing/reports/l1`, { params });
    return res.data;
  }, [financer, projected, lastMonth, search, page]);

  const doExport = async () => {
    let path = `/leasing/reports/l1/export?financer=${financer}&projected=${projected}`;
    if (lastMonth) path += `&last_month=${lastMonth}`;
    try {
      await downloadFile(path, `L1_${financer}.xlsx`);
    } catch (e) {
      toast.error("Export eșuat");
    }
  };

  const months = data?.months || [];
  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Ultima lună (LL-AAAA)</Label>
          <MonthInput value={lastMonth} onChange={(v) => { setLastMonth(v); setPage(1); }} className="w-40" data-testid="l1-last-month" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Caută contract</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="l1-search-input" placeholder="Nr contract…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8 w-52" />
          </div>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch checked={projected} onCheckedChange={(v) => { setProjected(v); setPage(1); }} data-testid="l1-projected-toggle" />
          <Label className="text-sm">Arată lunile proiectate</Label>
        </div>
        {loading && <span className="flex items-center gap-1 text-xs text-slate-400 pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> se calculează…</span>}
        <Button onClick={doExport} variant="outline" className="gap-2 ml-auto" data-testid="l1-export-btn">
          <Download className="h-4 w-4" /> Export Excel
        </Button>
      </div>

      {projected && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
          Proiecție activă: lunile viitoare folosesc principalul programat (nu identic cu Excel).
        </div>
      )}

      {error ? (
        <ReportError onRetry={reload} />
      ) : loading && !data ? (
        <ReportLoading />
      ) : (
        <>
          <div className="border rounded-md overflow-auto max-h-[65vh] relative">
            <table className="text-xs jh-table" data-testid="report-l1-table">
              <thead className="sticky top-0 z-10 bg-slate-100">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-slate-100 z-20 min-w-[130px]">Contract</th>
                  {months.map((m) => (
                    <th key={m} className="px-3 py-2 text-right whitespace-nowrap border-l" colSpan={2}>{fmtMonth(m)}</th>
                  ))}
                </tr>
                <tr>
                  <th className="sticky left-0 bg-slate-100 z-20"></th>
                  {months.map((m) => (
                    <Fragment key={m + "sub"}>
                      <th className="px-3 py-1 text-right text-[10px] font-medium text-slate-500 border-l">RATA CAP.</th>
                      <th className="px-3 py-1 text-right text-[10px] font-medium text-slate-500">VAL. RĂMASĂ</th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-amber-50/40">
                    <td className="px-3 py-1.5 font-medium sticky left-0 bg-white z-10 whitespace-nowrap">{r.display_code}</td>
                    {r.series.map((s, i) => (
                      <Fragment key={i}>
                        <td className={`px-3 py-1.5 text-right tabular border-l ${s.closed ? "" : "text-slate-400"}`}>{fmtNumber(s.rata)}</td>
                        <td className="px-3 py-1.5 text-right tabular">{fmtNumber(s.ramasa)}</td>
                      </Fragment>
                    ))}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td className="px-4 py-10 text-center text-slate-400">Niciun contract.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">{total} contracte</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="l1-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="tabular">{page} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="l1-next-page"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
